import { HeadContent, Outlet, Scripts, createRootRoute } from '@tanstack/react-router';
import * as React from 'react';
import { match } from 'ts-pattern';

import { PageError } from '#/components/pages';
import config from '#/config';
import { getAnalytics } from '#/lib/analytics';
import { scriptTag, seo } from '#/lib/head';
import { PrivacyConfirm } from '#/lib/privacy';
import { GoogleNoScript, googleTagManagerScripts } from '#/vendors/google-tag-manager';
import { KoalaProvider } from '#/vendors/koala';
import { PostHogProvider } from '#/vendors/posthog';

import appCss from '../index.css?url';

export const Route = createRootRoute({
  head: () => {
    return {
      meta: [
        {
          charSet: 'utf-8',
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1.0',
        },
        match(config.environment)
          .when(
            (env) => env !== 'production',
            () => ({
              name: 'robots',
              content: 'noindex, nofollow',
            })
          )
          .otherwise(() => undefined),
        ...seo({
          title: 'Joggr gives a 🦆 (duck) about docs.',
          description:
            'Joggr is the documentation platform built from the ground up specifically for developers & software teams.',
        }),
      ],
      links: [
        ...preconnectLinks([
          'http://js-na1.hs-scripts.com',
          'https://js.hs-scripts.com',
          'https://us.i.posthog.com',
          'https://us-assets.i.posthog.com',
          'https://fonts.gstatic.com',
          'https://www.googletagmanager.com',
        ]),
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '96x96',
          href: '/favicon-96x96.png',
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '32x32',
          href: '/favicon-32x32.png',
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '16x16',
          href: '/favicon-16x16.png',
        },
        { rel: 'icon', href: '/favicon.ico' },
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap',
        },
        { rel: 'stylesheet', href: appCss },
      ],
      scripts: [...googleTagManagerScripts(), ...devScripts()],
    };
  },
  onCatch(error) {
    try {
      const analytics = getAnalytics();
      analytics.captureException(error, { reason: 'root.onCatch' });
    } catch (error) {
      console.error(error, 'Unable to capture exception');
    }
  },
  notFoundComponent: (props) => (
    <RootDocument>
      <PageError {...props} type='not-found' />
    </RootDocument>
  ),
  errorComponent: (props) => (
    <RootDocument>
      <PageError {...props} type='default' />
    </RootDocument>
  ),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
      <PrivacyConfirm />
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <React.StrictMode>
      <html lang='en' className='riker-theme-dark' data-theme='quarks'>
        <head suppressHydrationWarning>
          <HeadContent />
        </head>
        <body style={{ background: 'oklch(11.63% 0.0062 285.4)' }}>
          <KoalaProvider>
            <PostHogProvider>
              <React.Suspense fallback={null}>
                <div className='Root'>{children}</div>
              </React.Suspense>
            </PostHogProvider>
          </KoalaProvider>
          <RouterDevtools position='bottom-left' />
          <Scripts />
          <GoogleNoScript />
        </body>
      </html>
    </React.StrictMode>
  );
}

/*
|------------------
| Internals
|------------------
*/

/**
 * Get internal scripts for development.
 *
 * @returns internal scripts for development
 */
function devScripts() {
  if (config.environment === 'local') {
    return [
      // Hack to fix React Fast Refresh in Vite in development
      // @see https://github.com/TanStack/router/issues/1992
      scriptTag({
        type: 'module',
        children: `
  import RefreshRuntime from "/_build/@react-refresh";
  RefreshRuntime.injectIntoGlobalHook(window)
  window.$RefreshReg$ = () => {}
  window.$RefreshSig$ = () => (type) => type
      `.trim(),
      }),
    ];
  }

  return [];
}

/**
 * Create preconnect and dns-prefetch links.
 */
function preconnectLinks(links: string[]) {
  return links.flatMap((href) => [
    { rel: 'preconnect', href },
    { rel: 'dns-prefetch', href },
  ]);
}

/**
 * Router devtools.
 */
const RouterDevtools =
  config.environment !== 'local'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        }))
      );
